import { lazy, Suspense } from "react";
import { CLOUD_IMPORT_ENABLED } from "@/lib/uploader/cloudImportFlag";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/hooks/useTheme";
import { CognitoAuthProvider } from "@/hooks/useCognitoAuth";
import { UploadManagerProvider } from "@/hooks/useUploadManager";
import UploadIndicator from "@/components/workspace/UploadIndicator";
import UploaderTabBridge from "@/components/workspace/uploader/UploaderTabBridge";
import SessionExpiredBanner from "@/components/workspace/SessionExpiredBanner";
import ScanAdminBinder from "@/components/scan/ScanAdminBinder";
import ErrorBoundary from "@/components/ErrorBoundary";
import RequireRole from "@/components/workspace/RequireRole";
import WorkspaceLanding from "@/components/workspace/WorkspaceLanding";
import { ADMIN_ROLES } from "@/hooks/useRole";
import ClientOnly from "@/components/ClientOnly";
import RouteChangeUnlock from "@/components/RouteChangeUnlock";
import ScrollToTop from "@/components/ScrollToTop";
import { DialogProvider } from "@/components/ui/confirm-dialog";
import HostTenantRedirect from "@/components/HostTenantRedirect";
import AdminBaseEscape from "@/components/AdminBaseEscape";
import RootRoute from "@/components/RootRoute";
import { detectHostTenant } from "@/lib/hostTenant";
import RequireMfa from "./components/auth/RequireMfa";
import Intercom from "./components/Intercom";
import EnvBadge from "./components/EnvBadge";
import DefaultMeta from "./components/DefaultMeta";
import RouteMeta from "./components/RouteMeta";
import CookieConsent from "./components/CookieConsent";
import "./i18n";

// --- Public routes: statically imported so they are server-rendered. ---
// NOTE: the v2 public gallery/browse surface (TenantPages, TenantPage,
// GalleryViewer, FindPhotos, CompanyGalleries) is intentionally NOT routed for
// now — v2 is admin-only while the public gallery stays on the legacy app. The
// components are kept in the repo so the routes can be restored when we return
// to the new public gallery. See App routes below.
import Suspended from "./pages/public/Suspended";
import About from "./pages/About";
import LegalPolicy from "./pages/LegalPolicy";
import BiometricRetentionPolicy from "./pages/BiometricRetentionPolicy";
import FaceDataPolicy from "./pages/FaceDataPolicy";
import FamilyFaceDataPolicy from "./pages/FamilyFaceDataPolicy";
import { FACE_DATA_POLICY_PATH, FAMILY_FACE_DATA_POLICY_PATH } from "./content/faceDataPolicy";
import ForPhotographers from "./pages/ForPhotographers";
import ForParticipants from "./pages/ForParticipants";
import PreviewProtection from "./pages/PreviewProtection";
import Pricing from "./pages/Pricing";
import Pricing2026 from "./pages/Pricing2026";
import PricingChanges2026 from "./pages/PricingChanges2026";
import FaceDataChanges2026 from "./pages/FaceDataChanges2026";
import PreviewFirstUploads from "./pages/PreviewFirstUploads";
import WebUploader from "./pages/WebUploader";
import OnDemandFtp from "./pages/OnDemandFtp";
import NotFound from "./pages/NotFound";
// The public gallery directory (the participant entry point; the ALB routes all apex traffic here). Static, not lazy,
// so it's server-rendered with its galleries as real links (src/lib/gallery/ssrPrefetch.ts).
import FindPhotos from "./pages/FindPhotos";
import CompanyGalleries from "./pages/CompanyGalleries";
// The new home page and the Photohawk Personal web pages: staging only until launch (src/lib/homeFlag.ts).
import { AppAliasRedirect, DownloadRoute, OldStudioRedirect, PersonalRoute, StudioRoute } from "./pages/home/HomeRoutes";
import PersonalLegalPage from "./pages/personal/PersonalLegalPage";
import DeleteAccountPage from "./pages/personal/DeleteAccountPage";
import SupportPage, { SUPPORT_PATH } from "./pages/personal/SupportPage";
// The guides hub: staging only until the owner approves it (src/lib/guidesFlag.ts); a real 404 in prod.
import GuidesIndex from "./pages/guides/GuidesIndex";
import GuidePage from "./pages/guides/GuidePage";
// The restored blog (src/content/blog): live everywhere, on its original URLs.
import { BlogIndexPage, BlogPostPage } from "./pages/blog/BlogPages";
import { EventPhotographyIndex, EventPhotographyPage } from "./pages/blog/EventPhotographyPages";

// --- Admin / auth / heavy routes: lazy so their browser-only deps (Cognito,
//     scan/upload, charts) stay out of the SSR bundle's synchronous graph and
//     load client-side on demand. ---
const Auth = lazy(() => import("./pages/Auth"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const UploadApp = lazy(() => import("./pages/UploadApp"));
const SignedInAgain = lazy(() => import("./pages/SignedInAgain"));
const Register = lazy(() => import("./pages/Register"));
const Account = lazy(() => import("./pages/account/Account"));
const Security = lazy(() => import("./pages/account/Security"));
const Connections = lazy(() => import("./pages/account/Connections"));
const PayeeEarnings = lazy(() => import("./pages/PayeeEarnings"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CreateWorkspace = lazy(() => import("./pages/CreateWorkspace"));
const TenantResolver = lazy(() => import("./pages/TenantResolver"));
const Admin = lazy(() => import("./pages/Admin"));
const WorkspaceLayout = lazy(() => import("./layouts/WorkspaceLayout"));
const WorkspaceDashboard = lazy(() => import("./pages/workspace/WorkspaceDashboard"));
const WorkspaceGalleries = lazy(() => import("./pages/workspace/WorkspaceGalleries"));
const WorkspaceGalleryDetail = lazy(() => import("./pages/workspace/WorkspaceGalleryDetail"));
const WorkspacePages = lazy(() => import("./pages/workspace/WorkspacePages"));
const WorkspacePageDetail = lazy(() => import("./pages/workspace/WorkspacePageDetail"));
const WorkspaceOrders = lazy(() => import("./pages/workspace/WorkspaceOrders"));
const WorkspaceCustomers = lazy(() => import("./pages/workspace/WorkspaceCustomers"));
const WorkspaceCoupons = lazy(() => import("./pages/workspace/WorkspaceCoupons"));
const WorkspaceProducts = lazy(() => import("./pages/workspace/WorkspaceProducts"));
const WorkspacePayouts = lazy(() => import("./pages/workspace/WorkspacePayouts"));
const WorkspaceMedia = lazy(() => import("./pages/workspace/WorkspaceMedia"));
const WorkspaceTeam = lazy(() => import("./pages/workspace/WorkspaceTeam"));
const WorkspaceBilling = lazy(() => import("./pages/workspace/WorkspaceBilling"));
const WorkspaceSettings = lazy(() => import("./pages/workspace/WorkspaceSettings"));
// Internal-staff platform area (cross-tenant overview + email campaigns).
const PlatformLayout = lazy(() => import("./layouts/PlatformLayout"));
const PlatformOverview = lazy(() => import("./pages/platform/PlatformOverview"));
const PlatformWorkspaces = lazy(() => import("./pages/platform/PlatformWorkspaces"));
const PlatformGalleries = lazy(() => import("./pages/platform/PlatformGalleries"));
const PlatformCustomers = lazy(() => import("./pages/platform/PlatformCustomers"));
const PlatformUsers = lazy(() => import("./pages/platform/PlatformUsers"));
const PlatformOrders = lazy(() => import("./pages/platform/PlatformOrders"));
const PlatformPayouts = lazy(() => import("./pages/platform/PlatformPayouts"));
const PlatformBalancesOwed = lazy(() => import("./pages/platform/PlatformBalancesOwed"));
const PlatformBilling = lazy(() => import("./pages/platform/PlatformBilling"));
const PlatformPayoutRecovery = lazy(() => import("./pages/platform/PlatformPayoutRecovery"));
const PlatformCampaigns = lazy(() => import("./pages/platform/PlatformCampaigns"));
const PlatformSegments = lazy(() => import("./pages/platform/PlatformSegments"));
const PlatformScanning = lazy(() => import("./pages/platform/PlatformScanning"));
const PlatformWhatsApp = lazy(() => import("./pages/platform/PlatformWhatsApp"));
const PlatformContentAudit = lazy(() => import("./pages/platform/PlatformContentAudit"));
const PlatformStoragePurgePlan = lazy(() => import("./pages/platform/PlatformStoragePurgePlan"));
const PlatformConnect = lazy(() => import("./pages/platform/PlatformConnect"));
const PlatformCustomDomains = lazy(() => import("./pages/platform/PlatformCustomDomains"));
const PlatformFaceReview = lazy(() => import("./pages/platform/PlatformFaceReview"));
const PlatformFaceLab = lazy(() => import("./pages/platform/PlatformFaceLab"));
const PlatformPricing = lazy(() => import("./pages/platform/PlatformPricing"));

/** A fresh QueryClient — one per request on the server, one for the app on the client. */
export const makeQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        // Don't refetch every time the browser tab regains focus — it caused a
        // disruptive full-screen refresh when switching back to the tab.
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
    },
  });

/**
 * `/admin` is context-dependent: on the app host (apex) it's the internal
 * marketing CMS; on a tenant host it's the workspace admin. There we resolve the
 * role-appropriate landing (owner/admin → dashboard, others → galleries) via
 * WorkspaceLanding rather than a fixed redirect.
 */
const AdminRoot = () =>
  detectHostTenant().type === "app" ? <Admin /> : <WorkspaceLanding />;

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<RootRoute />} />
    {/* The public participant entry point IS routed here — the apex ALB sends all
        apex traffic to this app, and the marketing nav links to /find-your-photos,
        so it must resolve here (otherwise every "Find Photos" CTA 404s). The rest
        of the public gallery/browse surface (/pages, /galleries/:slug) is still
        held on the legacy app; those components remain in the repo, restore their
        routes when returning to the new public gallery. */}
    {/* The new site's two products ("/" is the combined home; RootRoute): /studio (Photohawk Studio, for
        photographers and event businesses; its old URLs /events and /business are 301s to it), /download (the
        Studio app) and /personal (the Photohawk app, the phone app; /app is a 301 to it). Where the new home is off (prod until launch:
        src/lib/homeFlag.ts) they are real 404s and "/" is today's home page. */}
    <Route path="/studio" element={<StudioRoute />} />
    <Route path="/events" element={<OldStudioRedirect />} />
    <Route path="/business" element={<OldStudioRedirect />} />
    <Route path="/download" element={<DownloadRoute />} />
    <Route path="/personal" element={<PersonalRoute />} />
    <Route path="/app" element={<AppAliasRedirect />} />
    {/* The Photohawk Family app's own pages are public everywhere, production included, from 20 October 2026 (the App
        Store listing and the app link them): the privacy policy and terms (official v1.0), account deletion and
        support. */}
    <Route path="/personal/privacy" element={<PersonalLegalPage doc="privacy" />} />
    {/* The app's face data policy is official (20 October 2026) and lives at /face-data-policy/family, live everywhere.
        The app links /personal/face-data-policy: both old URLs are 301s (src/lib/seo/redirects.ts) and, in the
        browser, redirects here. */}
    <Route path="/personal/face-data" element={<Navigate to={FAMILY_FACE_DATA_POLICY_PATH} replace />} />
    <Route path="/personal/face-data-policy" element={<Navigate to={FAMILY_FACE_DATA_POLICY_PATH} replace />} />
    <Route path="/personal/terms" element={<PersonalLegalPage doc="terms" />} />
    <Route path="/personal/delete-account" element={<DeleteAccountPage />} />
    <Route path={SUPPORT_PATH} element={<SupportPage />} />
    <Route path="/find-your-photos" element={<FindPhotos />} />
    <Route path="/find-your-photos/:companyName" element={<CompanyGalleries />} />
    <Route path="/suspended" element={<Suspended />} />
    <Route path="/about" element={<About />} />

    <Route path="/login" element={<Auth />} />
    <Route path="/register" element={<Register />} />
    <Route path="/account" element={<Account />} />
    <Route path="/account/security" element={<Security />} />
    {/* Revenue split: an external payee's earnings via the signed link in their invite. */}
    <Route path="/earnings" element={<PayeeEarnings />} />
    <Route path="/account/connections" element={CLOUD_IMPORT_ENABLED ? <Connections /> : <Navigate to="/account" replace />} />

    {/* Tenant admin login (classic-style, host-resolved) + token handoff. */}
    <Route path="/admin/login" element={<AdminLogin />} />
    <Route path="/admin/signed-in" element={<SignedInAgain />} />
    {/* Standalone full-screen uploader (also the root of upload.photohawk.com). /admin/upload is the
        same page on a tenant / custom-domain host, where only /admin/* reaches this app — a gallery's
        Upload tab opens it there in its own tab (src/lib/uploader/uploaderTab.ts). */}
    <Route path="/upload" element={<UploadApp />} />
    <Route path="/admin/upload" element={<UploadApp />} />
    {/* Cross-tenant workspace chooser (app host). */}
    <Route path="/admin/workspaces" element={<Dashboard />} />
    <Route path="/admin/workspaces/new" element={<CreateWorkspace />} />
    <Route path="/admin/resolve" element={<TenantResolver />} />
    <Route path="/login/select-organization" element={<Dashboard />} />

    {/* /admin — internal marketing CMS on the app host, tenant admin on a
        tenant host (redirect to galleries). */}
    <Route path="/admin" element={<AdminRoot />} />
    {/* Legacy alias URLs no longer exist — the tenant is resolved from the host.
        Photos is folded into galleries. */}
    <Route path="/admin/photos" element={<Navigate to="/admin/galleries" replace />} />

    {/* Tenant admin (workspace resolved from the host — no slug in the path). */}
    <Route element={<WorkspaceLayout />}>
      {/* Owner/administrator dashboard — the default landing for those roles
          (see AdminRoot / WorkspaceLanding). RequireRole backs up the role-gated
          nav so other roles that reach the URL are redirected to galleries. */}
      <Route path="/admin/dashboard" element={<RequireRole allowed={ADMIN_ROLES}><WorkspaceDashboard /></RequireRole>} />
      {/* Galleries + Pages are visible to every role (gallery detail self-gates
          its tabs by role). The remaining workspace pages are owner/administrator
          only — RequireRole redirects other roles that reach the URL directly,
          backing up the role-filtered nav (matches the legacy admin gating). */}
      <Route path="/admin/galleries" element={<WorkspaceGalleries />} />
      <Route path="/admin/galleries/:slug" element={<WorkspaceGalleryDetail />} />
      {/* Pages is not part of the photographer's scope (Galleries-only) — guard the
          routes so a photographer can't reach them by typing the URL. */}
      <Route path="/admin/pages" element={<RequireRole allowed={["owner", "administrator", "salesperson"]}><WorkspacePages /></RequireRole>} />
      <Route path="/admin/pages/:slug" element={<RequireRole allowed={["owner", "administrator", "salesperson"]}><WorkspacePageDetail /></RequireRole>} />
      <Route path="/admin/media" element={<RequireRole allowed={ADMIN_ROLES}><WorkspaceMedia /></RequireRole>} />
      <Route path="/admin/billing" element={<RequireRole allowed={ADMIN_ROLES}><WorkspaceBilling /></RequireRole>} />
      <Route path="/admin/team" element={<RequireRole allowed={ADMIN_ROLES}><WorkspaceTeam /></RequireRole>} />
      <Route path="/admin/settings" element={<RequireRole allowed={ADMIN_ROLES}><WorkspaceSettings /></RequireRole>} />
      <Route path="/admin/orders" element={<RequireRole allowed={ADMIN_ROLES}><WorkspaceOrders /></RequireRole>} />
      <Route path="/admin/customers" element={<RequireRole allowed={ADMIN_ROLES}><WorkspaceCustomers /></RequireRole>} />
      <Route path="/admin/coupons" element={<RequireRole allowed={ADMIN_ROLES}><WorkspaceCoupons /></RequireRole>} />
      <Route path="/admin/products" element={<RequireRole allowed={ADMIN_ROLES}><WorkspaceProducts /></RequireRole>} />
      <Route path="/admin/payouts" element={<RequireRole allowed={ADMIN_ROLES}><WorkspacePayouts /></RequireRole>} />
    </Route>
    <Route path="/for-photographers" element={<ForPhotographers />} />
    <Route path="/for-participants" element={<ForParticipants />} />
    <Route path="/preview-protection" element={<PreviewProtection />} />
    <Route path="/pricing" element={<Pricing />} />
    {/* Pricing from 1 November 2026 (replaces /pricing at launch) and the "what's changing" page.
        /pricing-2026 is unlinked on purpose and noindex,nofollow until launch: don't add it to the
        nav, footer or a sitemap. /pricing-changes-2026 is public (indexable) from the 20 October
        announcement. Launch-day steps: src/pages/Pricing2026.tsx. */}
    <Route path="/pricing-2026" element={<Pricing2026 />} />
    <Route path="/pricing-changes-2026" element={<PricingChanges2026 />} />
    {/* "Selfie search and face data from 1 November 2026": public (indexable) and live on prod, NOT behind the new-home
        switch, because the face-data campaign email links to it, so this exact path must not change. */}
    <Route path="/face-data-changes-2026" element={<FaceDataChanges2026 />} />
    {/* "How preview-first uploads work" — linked from the web uploader, so this path must not change. */}
    <Route path="/preview-first-uploads" element={<PreviewFirstUploads />} />
    {/* "The new Photohawk web uploader" — linked from the launch email and the uploader, so this path must not change. */}
    <Route path="/web-uploader" element={<WebUploader />} />
    {/* "How on-demand FTP works" — linked from the FTP server panel, /web-uploader, the pricing pages and the
        shared-FTP reminder emails, so this path must not change. */}
    <Route path="/on-demand-ftp" element={<OnDemandFtp />} />
    {/* Guides (content in src/content/guides/<slug>/<lang>.md). Where the guides are off (prod until the owner
        approves them: src/lib/guidesFlag.ts) both are real 404s. */}
    <Route path="/guides" element={<GuidesIndex />} />
    <Route path="/guides/:slug" element={<GuidePage />} />
    {/* The old blog, restored on its original URLs (footer "Blog" link, links from 2023 onwards). NOT behind the
        guides flag; a post a guide now covers is a 301 to that guide where the guides are on. */}
    <Route path="/blog" element={<BlogIndexPage />} />
    <Route path="/blog/:slug" element={<BlogPostPage />} />
    {/* Event photography pages, by sport and for organisers (src/content/eventPhotography): live everywhere. */}
    <Route path="/event-photography" element={<EventPhotographyIndex />} />
    <Route path="/event-photography/:slug" element={<EventPhotographyPage />} />

    {/* Data-protection policy (GDPR/RGPD). /privacy is the canonical path;
        /rgpd, /gdpr and /data-protection are aliases to the same page (localised). */}
    <Route path="/privacy" element={<LegalPolicy />} />
    <Route path="/rgpd" element={<LegalPolicy />} />
    <Route path="/gdpr" element={<LegalPolicy />} />
    <Route path="/data-protection" element={<LegalPolicy />} />
    {/* Biometric data retention policy — linked from the 20 October 2026 announcement email and the
        pricing-changes page, so this exact path must not change. */}
    <Route path="/biometric-data-retention" element={<BiometricRetentionPolicy />} />
    {/* Photohawk's face data policy (owner, 20 October 2026): one public page, a tab per product (#studio, #family),
        and the full Photohawk Family policy. Live in production, NOT behind the new-home switch. */}
    <Route path={FACE_DATA_POLICY_PATH} element={<FaceDataPolicy />} />
    <Route path={FAMILY_FACE_DATA_POLICY_PATH} element={<FamilyFaceDataPolicy />} />

    {/* Internal-staff platform area — self-gated on the backend super-admin flag
        (PlatformLayout redirects non-staff). NOT workspace-scoped. */}
    <Route element={<PlatformLayout />}>
      <Route path="/platform" element={<PlatformOverview />} />
      <Route path="/platform/workspaces" element={<PlatformWorkspaces />} />
      <Route path="/platform/galleries" element={<PlatformGalleries />} />
      <Route path="/platform/customers" element={<PlatformCustomers />} />
      <Route path="/platform/users" element={<PlatformUsers />} />
      <Route path="/platform/orders" element={<PlatformOrders />} />
      <Route path="/platform/payouts" element={<PlatformPayouts />} />
      <Route path="/platform/balances-owed" element={<PlatformBalancesOwed />} />
      <Route path="/platform/billing" element={<PlatformBilling />} />
      <Route path="/platform/pricing" element={<PlatformPricing />} />
      <Route path="/platform/billing/recovery" element={<PlatformPayoutRecovery />} />
      <Route path="/platform/connect" element={<PlatformConnect />} />
      <Route path="/platform/custom-domains" element={<PlatformCustomDomains />} />
      <Route path="/platform/face-review" element={<PlatformFaceReview />} />
      <Route path="/platform/face-lab" element={<PlatformFaceLab />} />
      <Route path="/platform/campaigns" element={<PlatformCampaigns />} />
      <Route path="/platform/segments" element={<PlatformSegments />} />
      <Route path="/platform/scanning" element={<PlatformScanning />} />
      <Route path="/platform/whatsapp" element={<PlatformWhatsApp />} />
      <Route path="/platform/storage-audit" element={<PlatformContentAudit />} />
      <Route path="/platform/storage-purge-plan" element={<PlatformStoragePurgePlan />} />
    </Route>

    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}

    {/* The new admin serves /admin (the only admin; /v2/admin redirects here). An unmatched /admin/* path is
        almost always a bookmarked LEGACY-admin URL (old language-prefixed routes,
        retired pages) — send it to the new admin's default resolver instead of a
        404. AdminRoot picks the right landing (workspace chooser vs a tenant's
        default page). Specific /admin/* routes above still win (v6 route ranking). */}
    <Route path="/admin/*" element={<Navigate to="/admin" replace />} />

    <Route path="*" element={<NotFound />} />
  </Routes>
);

/**
 * The whole app minus the router (the router is supplied by the client entry as
 * BrowserRouter and by the server entry as StaticRouter). Browser-only chrome is
 * wrapped in <ClientOnly> so it never runs during SSR.
 */
export const AppShell = ({ queryClient }: { queryClient: QueryClient }) => (
  <ErrorBoundary scope="root">
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <CognitoAuthProvider>
          <TooltipProvider>
            <DefaultMeta />
            <ClientOnly>
              <Toaster />
              <Sonner />
              <Intercom />
              <EnvBadge />
              <CookieConsent />
            </ClientOnly>
            <UploadManagerProvider>
              <ClientOnly>
                <UploadIndicator />
                <UploaderTabBridge />
                <SessionExpiredBanner />
                {/* On-device-scan debug surface — andy@photohawk.io only. Also pushes
                    the admin verdict into the plain scan modules for gating. */}
                <ScanAdminBinder />
              </ClientOnly>
              <RouteChangeUnlock />
              <ScrollToTop />
              <HostTenantRedirect />
              <AdminBaseEscape />
              <DialogProvider>
                <RequireMfa>
                  <ErrorBoundary scope="app">
                    <Suspense fallback={null}>
                      <AppRoutes />
                    </Suspense>
                  </ErrorBoundary>
                </RequireMfa>
                {/* After the routes so a public page's server-rendered head (title, canonical,
                    hreflang, Open Graph, JSON-LD) wins over the page's own and DefaultMeta. */}
                <RouteMeta />
              </DialogProvider>
            </UploadManagerProvider>
          </TooltipProvider>
        </CognitoAuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

/** Client-side app: Helmet + BrowserRouter + the shell with a single QueryClient. */
const clientQueryClient = makeQueryClient();

const App = () => (
  <HelmetProvider>
    <BrowserRouter>
      <AppShell queryClient={clientQueryClient} />
    </BrowserRouter>
  </HelmetProvider>
);

export default App;
